// Create and delete sensor classes: the cold_class value (names in four
// languages, {regime, target}), its rule group and its alarm rules - the
// same convention as tools/setup-rules.py / setup-classes.py.
import { tenantApi } from "../lib/account";
import { HAS, TEMP } from "./rules";

export interface NewClass { key: string; names: Record<string, string>; regime: string; target: number; warn: number; alarm: number; crit: number; low: number | null; delay: number; hyst: number }
type Texts = Record<string, Record<string, { text: string; state?: string }>>;

const rule = (name: string, cond: string, alarm: string, sev: string, forMin: number, clear: string) => ({
  name, enabled: true, trigger: { on: "uplink" }, condition: `${HAS} && ${cond}`,
  timing: { for: `${forMin}m`, clear: `${HAS} && ${clear}` }, actions: [{ type: "alarm", alarm, severity: sev }],
});

async function putDef(mut: (values: string[], texts: Texts) => void) {
  const def = await tenantApi<Record<string, unknown> & { enum_values: string[]; texts?: Texts }>("GET", "/property-defs/cold_class?texts=all");
  const values = [...def.enum_values]; const texts: Texts = JSON.parse(JSON.stringify(def.texts || {}));
  mut(values, texts);
  const keep = ["label", "data_type", "unit", "type_ids", "required", "is_unique", "indexed", "groupable", "in_list", "section", "sort", "options", "source_lang", "visibility", "allowed_values"];
  await tenantApi("PUT", "/property-defs/cold_class", { ...Object.fromEntries(keep.map((k) => [k, def[k]])), enum_values: values, texts });
}

export async function createClass(c: NewClass) {
  await putDef((values, texts) => {
    if (!values.includes(c.key)) values.push(c.key);
    for (const [l, n] of Object.entries(c.names)) if (n) (texts[l] ||= {})[`value.${c.key}`] = { text: n, state: "reviewed" };
  });
  await tenantApi("PUT", `/labels/cold_class/values/${c.key}`, { display_name: c.names.en, data: { regime: c.regime, target: c.target }, texts: Object.fromEntries(Object.entries(c.names).filter(([l, n]) => l !== "en" && n).map(([l, n]) => [l, { text: n }])) });
  const g = await tenantApi<{ id: string }>("POST", "/groups", { name: `Class ${c.key}`, kind: "rule", rule: `device.type_id == "ch.gimasi.vst" && str(labels, "cold_class", "") == "${c.key}"`, priority: 0 });
  const f = (v: number) => v.toFixed(1);
  const defs = [
    rule(`${c.key}: above ${c.warn} °C (warning)`, `${TEMP} > ${f(c.warn)}`, "TEMP_WARNING", "warning", Math.max(5, Math.round(c.delay * 0.75)), `${TEMP} <= ${f(c.warn - c.hyst)}`),
    rule(`${c.key}: above ${c.alarm} °C (alarm)`, `${TEMP} > ${f(c.alarm)}`, "TEMP_HIGH", "alarm", c.delay, `${TEMP} <= ${f(c.warn)}`),
    rule(`${c.key}: above ${c.crit} °C (critical)`, `${TEMP} > ${f(c.crit)}`, "TEMP_CRITICAL", "critical", Math.max(5, Math.round(c.delay / 2)), `${TEMP} <= ${f(c.alarm)}`),
    ...(c.low != null ? [rule(`${c.key}: below ${c.low} °C (too cold)`, `${TEMP} < ${f(c.low)}`, "TEMP_LOW", "warning", 30, `${TEMP} >= ${f(c.low + c.hyst)}`)] : []),
  ];
  const ids: string[] = [];
  for (const d of defs) ids.push((await tenantApi<{ id: string }>("POST", "/rules", d)).id);
  await tenantApi("PUT", `/groups/${g.id}/rules`, { rule_ids: ids });
}

export async function deleteClass(key: string, groupId: string | undefined, ruleIds: string[]) {
  if (groupId) await tenantApi("PUT", `/groups/${groupId}/rules`, { rule_ids: [] });
  for (const id of ruleIds) await tenantApi("DELETE", `/rules/${id}`);
  if (groupId) await tenantApi("DELETE", `/groups/${groupId}`);
  await putDef((values, texts) => {
    const i = values.indexOf(key); if (i >= 0) values.splice(i, 1);
    for (const l of Object.keys(texts)) delete texts[l][`value.${key}`];
  });
}
